import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Stethoscope, 
  User, 
  Calendar, 
  Bed, 
  FileText,
  Eye,
  Activity,
  Clock,
  Pill
} from 'lucide-react';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ipdApi } from '@/api/ipd';
import { ipdBillingApi } from '@/api/ipdBilling';
import { IPDAdmission, Ward } from '@/types/ipd';
import IPDVisitsList from '@/components/ipd/IPDVisitsList';
import type { IPDChargeableItem } from '@/types/ipdBilling';
import { ensureDoctorPrefix } from '@/lib/doctorName';

function NurseChargeableItemsContent({
  admissionId,
  chargeableForm,
  setChargeableForm,
  onClose,
  queryClient,
}: {
  admissionId: string;
  chargeableForm: { sourceType: 'PHARMA' | 'CONSUMABLE'; chargeCode: string; description: string; quantity: string; unitRate: string; serviceDate: string };
  setChargeableForm: React.Dispatch<React.SetStateAction<typeof chargeableForm>>;
  onClose: () => void;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const { data: itemsRes } = useQuery({
    queryKey: ['ipd-chargeable', admissionId],
    queryFn: () => ipdBillingApi.listChargeableItems(admissionId),
    enabled: !!admissionId,
  });
  const items: IPDChargeableItem[] = itemsRes?.data?.data ?? [];
  const addMut = useMutation({
    mutationFn: (data: { sourceType: 'PHARMA' | 'CONSUMABLE'; chargeCode: string; description: string; quantity: number; unitRate: number; serviceDate?: string }) =>
      ipdBillingApi.addChargeableItem(admissionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipd-chargeable', admissionId] });
      setChargeableForm({ sourceType: 'PHARMA', chargeCode: '', description: '', quantity: '1', unitRate: '', serviceDate: new Date().toISOString().slice(0, 10) });
      toast.success('Item added. Receptionist will include it in the bill when running accrual.');
    },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to add item'),
  });
  const formatDate = (s: string) => (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
  const formatCurrency = (n: number) => `₹${Number(n).toFixed(2)}`;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">Items you add here are included in the patient&apos;s IPD bill when the receptionist runs accrual.</p>
      <div>
        <Label className="text-sm font-medium">Existing items</Label>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">None yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{formatDate(c.serviceDate)}</TableCell>
                  <TableCell>{c.sourceType}</TableCell>
                  <TableCell>{c.chargeCode}</TableCell>
                  <TableCell>{c.description}</TableCell>
                  <TableCell className="text-right">{c.quantity}</TableCell>
                  <TableCell className="text-right">{formatCurrency(c.unitRate)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(c.quantity * c.unitRate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <div className="border-t pt-4 space-y-3">
        <Label className="text-sm font-medium">Add item</Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={chargeableForm.sourceType} onValueChange={(v: 'PHARMA' | 'CONSUMABLE') => setChargeableForm((f) => ({ ...f, sourceType: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="PHARMA">Medicine (PHARMA)</SelectItem><SelectItem value="CONSUMABLE">Consumable</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Charge code</Label>
            <Input placeholder="e.g. MED_001" value={chargeableForm.chargeCode} onChange={(e) => setChargeableForm((f) => ({ ...f, chargeCode: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Description</Label>
            <Input placeholder="e.g. Paracetamol 500mg" value={chargeableForm.description} onChange={(e) => setChargeableForm((f) => ({ ...f, description: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Quantity</Label>
            <Input type="number" min="1" value={chargeableForm.quantity} onChange={(e) => setChargeableForm((f) => ({ ...f, quantity: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Unit rate (₹)</Label>
            <Input type="number" step="0.01" value={chargeableForm.unitRate} onChange={(e) => setChargeableForm((f) => ({ ...f, unitRate: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Service date</Label>
            <Input type="date" value={chargeableForm.serviceDate} onChange={(e) => setChargeableForm((f) => ({ ...f, serviceDate: e.target.value }))} />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button
          onClick={() => {
            const qty = parseInt(chargeableForm.quantity, 10);
            const rate = parseFloat(chargeableForm.unitRate);
            if (!chargeableForm.chargeCode.trim() || !chargeableForm.description.trim() || isNaN(qty) || qty < 1 || isNaN(rate) || rate < 0) {
              toast.error('Fill code, description, quantity ≥ 1 and unit rate ≥ 0');
              return;
            }
            addMut.mutate({
              sourceType: chargeableForm.sourceType,
              chargeCode: chargeableForm.chargeCode.trim(),
              description: chargeableForm.description.trim(),
              quantity: qty,
              unitRate: rate,
              serviceDate: chargeableForm.serviceDate || undefined,
            });
          }}
          disabled={addMut.isPending}
        >
          {addMut.isPending ? 'Adding...' : 'Add item'}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function NurseIPDManagement() {
  const [allAdmissions, setAllAdmissions] = useState<IPDAdmission[]>([]); // Store all admissions
  const [admissions, setAdmissions] = useState<IPDAdmission[]>([]);
  const [filteredAdmissions, setFilteredAdmissions] = useState<IPDAdmission[]>([]);
  const [assignedWards, setAssignedWards] = useState<Ward[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAdmission, setSelectedAdmission] = useState<IPDAdmission | null>(null);
  const [isVisitsModalOpen, setIsVisitsModalOpen] = useState(false);
  const [selectedWardType, setSelectedWardType] = useState<string>('ALL');
  const [selectedRoom, setSelectedRoom] = useState<string>('ALL');
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [isChargeableModalOpen, setIsChargeableModalOpen] = useState(false);
  const [selectedAdmissionForChargeable, setSelectedAdmissionForChargeable] = useState<IPDAdmission | null>(null);
  const [chargeableForm, setChargeableForm] = useState({
    sourceType: 'PHARMA' as 'PHARMA' | 'CONSUMABLE',
    chargeCode: '',
    description: '',
    quantity: '1',
    unitRate: '',
    serviceDate: new Date().toISOString().slice(0, 10),
  });
  const queryClient = useQueryClient();

  // Fetch assigned wards
  const fetchAssignedWards = async () => {
    try {
      // TODO: Implement nurse ward assignment API
      // For now, we'll fetch all wards and assume nurse has access to all
      const response = await ipdApi.getWards();
      setAssignedWards(response.data.data);
    } catch (error) {
      console.error('Error fetching assigned wards:', error);
      toast.error('Failed to fetch assigned wards');
    }
  };

  // Fetch admissions in assigned wards
  const fetchAdmissions = async () => {
    try {
      setIsLoading(true);
      const response = await ipdApi.getAdmissions({ status: 'ADMITTED' });
      console.log(response.data.data);
      
      // Store all admissions for filter options
      setAllAdmissions(response.data.data);
      
      // Filter admissions to show only those in assigned wards
      const wardAdmissions = response.data.data.filter(
        () => {
          // If no ward assignment system, show all admissions
          // TODO: Implement proper nurse ward assignment filtering
          return true;
        }
      );
      
      setAdmissions(wardAdmissions);
      setFilteredAdmissions(wardAdmissions);
    } catch (error) {
      console.error('Error fetching admissions:', error);
      toast.error('Failed to fetch admissions');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter admissions by ward and status
  const filterAdmissions = () => {
    let filtered = admissions;

    // Filter by ward type
    if (selectedWardType !== 'ALL') {
      filtered = filtered.filter(admission => 
        admission.wardType === selectedWardType
      );
    }

    // Filter by room
    if (selectedRoom !== 'ALL') {
      filtered = filtered.filter(admission => 
        admission.roomNumber === selectedRoom
      );
    }

    // Filter by status
    if (selectedStatus !== 'ALL') {
      filtered = filtered.filter(admission => 
        admission.status === selectedStatus
      );
    }

    setFilteredAdmissions(filtered);
  };

  const handleWardTypeFilterChange = (wardType: string) => {
    setSelectedWardType(wardType);
    setSelectedRoom('ALL'); // Reset room selection when ward type changes
  };

  const handleStatusFilterChange = (status: string) => {
    setSelectedStatus(status);
  };

  // Get available rooms based on selected ward type
  const getAvailableRooms = () => {
    if (selectedWardType === 'ALL') {
      return Array.from(new Set(allAdmissions.map(p => p.roomNumber).filter(Boolean)));
    }
    
    // Filter patients by selected ward type first, then get unique rooms
    const wardTypePatients = allAdmissions.filter(p => p.wardType === selectedWardType);
    return Array.from(new Set(wardTypePatients.map(p => p.roomNumber).filter(Boolean)));
  };

  // Get ward sub type for a specific room
  const getRoomWardSubType = (roomNumber: string) => {
    const patient = allAdmissions.find(p => p.roomNumber === roomNumber);
    return patient?.wardSubType || '';
  };

  useEffect(() => {
    fetchAssignedWards();
  }, []);

  useEffect(() => {
    if (assignedWards.length > 0) {
      fetchAdmissions();
    }
  }, [assignedWards]);

  useEffect(() => {
    filterAdmissions();
  }, [selectedWardType, selectedRoom, selectedStatus, admissions]);

  const handleViewVisits = (admission: IPDAdmission) => {
    setSelectedAdmission(admission);
    setIsVisitsModalOpen(true);
  };

  const getWardTypeColor = (type: string) => {
    switch (type) {
      case 'ICU': return 'bg-red-100 text-red-800';
      case 'GENERAL': return 'bg-blue-100 text-blue-800';
      case 'PRIVATE': return 'bg-green-100 text-green-800';
      case 'SEMI_PRIVATE': return 'bg-yellow-100 text-yellow-800';
      case 'EMERGENCY': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ADMITTED': return 'bg-green-100 text-green-800';
      case 'DISCHARGED': return 'bg-gray-100 text-gray-800';
      case 'TRANSFERRED': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading patient data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tabular-nums text-artis-brown-dark">Nurse IPD Management</h1>
          <p className="text-sm text-artis-slate mt-1">Manage patients in your assigned wards</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-artis-slate">
            {filteredAdmissions.length} of {admissions.length} patients
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-3 text-sm text-gray-600">
            <p>Step 1: Select ward type → Step 2: Select room → View patients with bed assignments</p>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Ward Type:</label>
              <Select value={selectedWardType} onValueChange={handleWardTypeFilterChange}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by ward type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Ward Types</SelectItem>
                  {Array.from(new Set(allAdmissions.map(a => a.wardType))).map((wardType) => (
                    <SelectItem key={wardType} value={wardType}>
                      {wardType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Room:</label>
              <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="All Rooms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Rooms</SelectItem>
                  {getAvailableRooms().map((room) => {
                    const wardSubType = getRoomWardSubType(room!);
                    return (
                      <SelectItem key={room} value={room!}>
                        {room} {wardSubType ? `(${wardSubType})` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Status:</label>
              <Select value={selectedStatus} onValueChange={handleStatusFilterChange}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Status</SelectItem>
                  <SelectItem value="ADMITTED">Admitted</SelectItem>
                  <SelectItem value="DISCHARGED">Discharged</SelectItem>
                  <SelectItem value="TRANSFERRED">Transferred</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-blue-600" />
            Ward Patients
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredAdmissions.length === 0 ? (
            <div className="text-center py-8">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {selectedWardType === 'ALL' && selectedRoom === 'ALL' ? 'No patients in assigned wards' : 'No patients found'}
              </h3>
              <p className="text-gray-500">
                {selectedWardType === 'ALL' && selectedRoom === 'ALL'
                  ? 'No patients are currently admitted in your assigned wards' 
                  : (() => {
                      const wardText = selectedWardType === 'ALL' ? 'assigned wards' : `${selectedWardType} ward`;
                      const roomText = selectedRoom === 'ALL' ? '' : ` in ${selectedRoom}`;
                      return `No patients are currently admitted in ${wardText}${roomText}`;
                    })()
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Ward</TableHead>
                    <TableHead>Bed</TableHead>
                    <TableHead>Doctor</TableHead>
                    <TableHead>Admission Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAdmissions.map((admission) => (
                    <TableRow key={admission.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{admission.queue.patient.name}</div>
                          <div className="text-sm text-artis-slate">
                            {admission.queue.patient.gender}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {admission.thirdPartyHospitalName ? (
                            <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 border border-amber-200">
                              Third party: {admission.thirdPartyHospitalName}
                            </span>
                          ) : (
                          <div className="flex items-center gap-2">
                            <Badge className={getWardTypeColor(admission.wardType)}>
                              {admission.wardType}
                            </Badge>
                            {admission.wardSubType && (
                              <Badge variant="outline" className="text-xs">
                                {admission.wardSubType}
                              </Badge>
                            )}
                          </div>
                          )}
                          {!admission.thirdPartyHospitalName && admission.roomNumber && (
                            <div className="text-xs text-gray-500">
                              {admission.roomNumber}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {admission.thirdPartyHospitalName ? (
                          <span className="text-gray-400 text-sm">—</span>
                        ) : admission.bedNumber ? (
                          <div className="flex items-center gap-1">
                            <Bed className="h-4 w-4 text-gray-500" />
                            <span className="text-sm">{admission.bedNumber}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">No bed assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{ensureDoctorPrefix(admission.assignedDoctor.name)}</div>
                          <div className="text-sm text-artis-slate">{admission.assignedDoctor.specialisation}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{formatDate(admission.admissionDate)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(admission.status)}>
                          {admission.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewVisits(admission)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Clock className="h-4 w-4 mr-1" />
                            Visits
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedAdmissionForChargeable(admission);
                              setChargeableForm({
                                sourceType: 'PHARMA',
                                chargeCode: '',
                                description: '',
                                quantity: '1',
                                unitRate: '',
                                serviceDate: new Date().toISOString().slice(0, 10),
                              });
                              setIsChargeableModalOpen(true);
                            }}
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                          >
                            <Pill className="h-4 w-4 mr-1" />
                            Medicines / Consumables
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/ipd/patient/${admission.id}`, '_blank')}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Details
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Visits Modal */}
      <Dialog open={isVisitsModalOpen} onOpenChange={setIsVisitsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Patient Visits - {selectedAdmission?.queue.patient.name}
            </DialogTitle>
          </DialogHeader>
          {selectedAdmission && (
            <IPDVisitsList 
              admissionId={selectedAdmission.id}
              patientName={selectedAdmission.queue.patient.name}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Medicines / Consumables modal – logged items flow to IPD billing when receptionist runs accrual */}
      <Dialog open={isChargeableModalOpen} onOpenChange={setIsChargeableModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pill className="h-5 w-5" />
              Medicines / Consumables – {selectedAdmissionForChargeable?.queue?.patient?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedAdmissionForChargeable && (
            <NurseChargeableItemsContent
              admissionId={selectedAdmissionForChargeable.id}
              chargeableForm={chargeableForm}
              setChargeableForm={setChargeableForm}
              onClose={() => {
                setIsChargeableModalOpen(false);
                setSelectedAdmissionForChargeable(null);
              }}
              queryClient={queryClient}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
